<script setup lang="ts">
import { computed } from 'vue'

import ErrorMessage from '@/components/ErrorMessage.vue'
import OfflineBanner from '@/components/OfflineBanner.vue'
import PreviewCard from '@/components/PreviewCard.vue'
import PreviewSkeleton from '@/components/PreviewSkeleton.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import UrlInput from '@/components/UrlInput.vue'
import { useConversion } from '@/composables/useConversion'
import { formatBytes } from '@/lib/format'

const {
  url,
  state,
  preview,
  progress,
  result,
  error,
  online,
  reconnecting,
  stage,
  percent,
  queuePosition,
  setUrl,
  handlePaste,
  submit,
  convert,
  retry,
  reset,
  triggerDownload,
} = useConversion()

const showSkeleton = computed(() => state.value === 'previewing')
const showStandaloneError = computed(() => state.value === 'error' && preview.value === null)
const showInlineError = computed(() => state.value === 'error' && preview.value !== null)

const inputHint = computed(() =>
  url.value.trim() === '' && state.value === 'idle'
    ? 'Funciona com links normais, do Shorts e do YouTube Music'
    : null,
)

/**
 * Single polite announcement point for the whole state machine.
 *
 * State transitions only — never the running percentage. The backend emits a
 * progress event per 1%, and a polite live region queues every change it sees,
 * so announcing the number would read out up to a hundred messages per
 * download and fall so far behind that "Ready" arrives long after the file
 * has. The `role="progressbar"` node carries the live value in
 * `aria-valuenow`/`aria-valuetext` for anyone who wants it.
 */
const statusMessage = computed(() => {
  switch (state.value) {
    case 'idle':
      return ''
    case 'previewing':
      return 'Buscando os dados do vídeo.'
    case 'previewed':
      return preview.value
        ? `Vídeo encontrado: ${preview.value.title}. Pronto para converter.`
        : 'Pronto para converter.'
    case 'converting':
      if (reconnecting.value) return 'Conexão perdida. Reconectando.'
      if (stage.value === 'queued') {
        return queuePosition.value > 0
          ? `Na fila, posição ${queuePosition.value}.`
          : 'Na fila para conversão.'
      }
      if (stage.value === 'downloading') return 'Baixando áudio.'
      return 'Convertendo para MP3.'
    case 'ready':
      return result.value
        ? `Pronto. ${result.value.filename}, ${formatBytes(result.value.sizeBytes)}. Download iniciado.`
        : 'Pronto. Download iniciado.'
    case 'error':
      return error.value ? `Erro. ${error.value.message}` : 'Algo deu errado.'
  }
})
</script>

<template>
  <OfflineBanner :visible="!online" />

  <div class="shell">
    <header class="masthead">
      <!-- The link only goes to `/`; the accessible name says just that. -->
      <a class="brand" href="/" aria-label="Signal — página inicial">
        <span class="brand__mark" aria-hidden="true">
          <i /><i /><i /><i />
        </span>
        <span class="brand__name">signal</span>
      </a>
    </header>

    <main class="main" id="main">
      <!-- One line of context, then the field. Anything else is preamble. -->
      <h1 class="title">Baixe o <span class="title__accent">áudio</span> de qualquer vídeo do YouTube</h1>

      <section class="console" aria-label="Converter um vídeo">
        <UrlInput
          :model-value="url"
          :busy="state === 'previewing'"
          :hint="inputHint"
          @update:model-value="setUrl($event)"
          @paste="handlePaste()"
          @submit="submit()"
        />

        <div class="stage">
          <PreviewSkeleton v-if="showSkeleton" />

          <PreviewCard v-else-if="preview" :preview="preview">
            <template #actions>
              <!-- The card never remounts; only this action area swaps. -->
              <button
                v-if="state === 'previewed'"
                type="button"
                class="btn btn--primary btn--block"
                @click="convert()"
              >
                Converter para MP3
                <svg
                  class="btn__arrow"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path d="M12 4v14M6 13l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>

              <ProgressBar
                v-else-if="state === 'converting'"
                :stage="stage"
                :percent="percent"
                :downloaded-bytes="progress?.downloadedBytes ?? 0"
                :total-bytes="progress?.totalBytes ?? 0"
                :queue-position="queuePosition"
                :reconnecting="reconnecting"
              />

              <div v-else-if="state === 'ready' && result" class="done">
                <div class="done__info">
                  <p class="done__file">{{ result.filename }}</p>
                  <p class="done__size mono">
                    {{ formatBytes(result.sizeBytes) }} · download iniciado
                  </p>
                </div>
                <div class="done__actions">
                  <button type="button" class="btn btn--primary done__again" @click="triggerDownload()">
                    Baixar de novo
                  </button>
                  <button type="button" class="btn btn--ghost done__new" @click="reset()">
                    Converter outro
                  </button>
                </div>
              </div>

              <ErrorMessage
                v-else-if="showInlineError && error"
                compact
                :code="error.code"
                :message="error.message"
                @retry="retry()"
              />
            </template>
          </PreviewCard>

          <ErrorMessage
            v-else-if="showStandaloneError && error"
            :code="error.code"
            :message="error.message"
            @retry="retry()"
          />
        </div>
      </section>
    </main>

    <footer class="foot">
      <!--
        The yt-dlp build and the queue depth used to sit here. They are a
        health readout for whoever runs the box, not something a visitor can
        act on; `GET /api/health` still serves both to the people who look.
      -->
      <p class="foot__note">Os arquivos são apagados uma hora depois da conversão.</p>
    </footer>
  </div>

  <p class="visually-hidden" role="status" aria-live="polite">{{ statusMessage }}</p>
</template>

<style scoped>
.shell {
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: clamp(1.5rem, 4.5vh, 2.75rem);
  min-height: 100svh;
  max-width: calc(var(--measure) + var(--gutter) * 2);
  margin-inline: auto;
  padding: 1.25rem var(--gutter) 2.5rem;
}

/* --- masthead ---------------------------------------------------------- */

.masthead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  text-decoration: none;
}

.brand__mark {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 1.125rem;
}

.brand__mark i {
  display: block;
  width: 2.5px;
  border-radius: 2px;
  background: var(--accent);
}

.brand__mark i:nth-child(1) {
  height: 40%;
}

.brand__mark i:nth-child(2) {
  height: 85%;
}

.brand__mark i:nth-child(3) {
  height: 60%;
}

.brand__mark i:nth-child(4) {
  height: 100%;
}

.brand__name {
  font-family: var(--font-display);
  font-size: var(--step-0);
  font-weight: 700;
  letter-spacing: -0.02em;
}

/* --- title ------------------------------------------------------------- */

.main {
  display: grid;
  gap: clamp(1.25rem, 3.5vh, 2rem);
  align-content: start;
}

/*
 * The whole preamble, in one line. It is deliberately a step smaller than a
 * display headline: the field below it is the thing to look at, and on a phone
 * a display-scale title pushed the input under the fold.
 */
.title {
  font-family: var(--font-display);
  font-size: var(--step-2);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.035em;
  text-wrap: balance;
  animation: rise 520ms var(--ease-out) both;
}

.title__accent {
  color: var(--accent);
}

/* --- console ----------------------------------------------------------- */

.console {
  display: grid;
  gap: 1.5rem;
  animation: rise 560ms var(--ease-out) 80ms both;
}

.stage {
  display: grid;
}

/* Idle: nothing under the field, so the field is not trailing dead space. */
.stage:empty {
  display: none;
}

.btn__arrow {
  width: 1.0625rem;
  height: 1.0625rem;
  transition: transform var(--dur) var(--ease);
}

.btn--primary:hover .btn__arrow {
  transform: translateY(2px);
}

/* --- done -------------------------------------------------------------- */

.done {
  display: grid;
  gap: 0.875rem;
  width: 100%;
}

.done__file {
  font-weight: 600;
  font-size: var(--step--1);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.done__size {
  margin-top: 0.125rem;
  font-size: var(--step--2);
  color: var(--accent);
}

.done__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.done__again {
  flex: 1 1 12rem;
  min-height: 3rem;
}

/* Narrow screens: the two buttons cannot share a line, and `flex-wrap` alone
   drops the ghost button onto its own row still at its natural width — next to
   a full-width primary that reads as a mistake rather than a hierarchy.
   A single-column grid stretches both, so the stack looks deliberate. */
@media (max-width: 30rem) {
  .done__actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .done__new {
    min-height: 3rem;
  }
}

.done__new {
  flex: 0 1 auto;
  min-height: 3rem;
  padding-inline: 1.125rem;
}

/* --- foot -------------------------------------------------------------- */

.foot {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  align-items: baseline;
  justify-content: space-between;
  padding-top: 1.5rem;
  border-top: 1px solid var(--line);
  color: var(--text-3);
  font-size: var(--step--1);
}

.foot__note {
  max-width: 42ch;
}
</style>
